import { Extension, Editor } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { NodeViewProps } from '@tiptap/react';

import {
  ExtendedFieldMappingEditorValue,
  getPlaceholderNodes,
  transformSimpleValueInput,
} from '@components/text-editors/addons/field-mapping';
import {
  createPlaceholderHtml,
  createPlaceholderNode,
  PlaceholderContainer,
} from '@components/text-editors/helpers';
import { validateEmail } from '@utils/validators';

export const tag = 'email-placeholder';

export const attributes = ['value'];

export const getPlaceholderHtml = createPlaceholderHtml({ tag, attributes });

const NodeComponent = (props: NodeViewProps) => {
  const email = props.node.attrs?.value;
  const isValid = validateEmail(email);
  const variant = isValid ? 'fixed' : 'error';

  return <PlaceholderContainer variant={variant} {...props} />;
};

const EmailNode = createPlaceholderNode({
  attributes,
  renderText: getPlaceholderHtml,
  tag,
  nodeComponent: NodeComponent,
});

type NodeInformation = {
  node: Node;
  position: number;
};

export const transformTextToEmailNode = (editor: Editor) => {
  const nodes: Array<NodeInformation> = [];
  editor.state.doc.descendants((node, position) => {
    nodes.push({ node, position });
  });
  const editorActions = editor.chain();
  // We reverse the order of the nodes, because we are deleting a range from the editor, and
  // that way we don't go out of bounds for subsequent nodes, since we are moving backward not forward
  nodes.reverse();
  nodes.forEach(({ node, position }) => {
    if (node.isText) {
      const email = node.textContent.trim();
      editorActions
        .deleteRange({ from: position, to: position + node.nodeSize })
        .insertContentAt(position, getPlaceholderHtml({ value: email }));
    }
  });
  editorActions.run();

  return true;
};

const BASE_EXTENSION_NAME = 'base_email';
const EMAIL_EXTENSION_NAME = 'email';

type EmailBaseOptions = {
  single?: boolean;
  emails?: string[];
};

export const EmailBaseExtension = Extension.create<EmailBaseOptions>({
  tag,
  addOptions: () => ({ single: false }),
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey(BASE_EXTENSION_NAME),

        filterTransaction: (transaction, state) => {
          const isAdding = state.doc.content.size < transaction.doc.content.size;
          if (!transaction.docChanged || !this.options.single || !isAdding) {
            return true;
          }
          const placeholderNodes = getPlaceholderNodes(state);
          const newPlaceholderNodes = getPlaceholderNodes(transaction);
          const newNode = newPlaceholderNodes.find(
            (node) => placeholderNodes.findIndex((n) => n === node) === -1
          );

          if (newNode) {
            transaction.deleteRange(0, transaction.doc.content.size - 1);
            transaction.insert(1, newNode);
            return true;
          }

          return placeholderNodes.length < 1;
        },
      }),
    ];
  },
  addKeyboardShortcuts(this) {
    const submitKeys = ['Enter', 'Space', ',', ';'];
    return submitKeys.reduce(
      (accumulated, key) => ({
        ...accumulated,
        [key]: () => transformTextToEmailNode(this.editor),
      }),
      {}
    );
  },
});

export const Email = EmailBaseExtension.extend({
  name: EMAIL_EXTENSION_NAME,
  addExtensions() {
    return [EmailNode];
  },
});

export const transformInput = (input: ExtendedFieldMappingEditorValue[]) =>
  transformSimpleValueInput(input, (value) =>
    getPlaceholderHtml({
      value,
    })
  );
